import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { areFilesValid } from '@platform/shared/utilities/file-utility';

import { RootState } from '@root/app.state';
import { selectNavigatedCustomerSpace } from '@core/core.selectors';
import { selectPortalConfig } from '@platform/platform.selectors';
import { ToastService } from '@platform/toast/toast.service';
import * as FiducaryActions from '@apps/fiduciary/fiduciary.actions';
import { selectFiduciaryFeature } from '@apps/fiduciary/fiduciary.selectors';
import { ResourceStatuses } from '@platform/shared/resource.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-payroll-widget',
  templateUrl: './payroll-widget.component.html',
  styleUrls: ['./payroll-widget.component.scss'],
  standalone: false,
})
export class PayrollWidgetComponent implements OnInit {
  @Input({ required: true }) customerSpaceId!: string;
  public isDragAndDropOverlayVisible = false;

  public uploadPending$!: Observable<boolean>;

  private destroyRef = inject(DestroyRef);
  private allowedFileFormats: string[] = [];
  private invalidFileAndFolderNameCharacters: string[] = [];

  constructor(
    private store$: Store<RootState>,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.uploadPending$ = this.store$.pipe(
      select(selectFiduciaryFeature),
      map((state) => state.abacusInboxes.payroll),
      map((state) => state.status === ResourceStatuses.PENDING),
    );

    this.store$
      .pipe(
        select(selectPortalConfig),
        filter((config) => !!config),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x) => {
        this.allowedFileFormats = x!.allowedFileUploadFormatsInbox;
        this.invalidFileAndFolderNameCharacters = x!.invalidFileAndFolderNameCharacters;
      });

    this.store$
      .pipe(
        select(selectNavigatedCustomerSpace),
        filter((state) => !!state.data),
        map((state) => state.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x) => (this.customerSpaceId = x.id));
  }

  public uploadPayrollDocuments(files: FileList) {
    const filesValid = areFilesValid(files, this.invalidFileAndFolderNameCharacters, this.allowedFileFormats);

    if (!filesValid.valid) {
      this.toastService.showErrorToast(filesValid.error!.translationKey, filesValid.error?.interpolateParams);
      return;
    }

    this.store$.dispatch(
      FiducaryActions.uploadFiduciaryInboxFiles({
        customerSpaceId: this.customerSpaceId,
        files,
        targetInbox: 'payroll',
      }),
    );
  }

  public showDragAndDropOverlay() {
    this.isDragAndDropOverlayVisible = true;
  }

  public hideDragAndDropOverlay() {
    this.isDragAndDropOverlayVisible = false;
  }

  public uploadFile(event: FileList) {
    this.uploadPayrollDocuments(event);
    this.hideDragAndDropOverlay();
  }
}
